import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { PageHeader } from '@/components/layout'
import { useAllBatches } from '@/hooks/useBatches'
import { useAuth } from '@/hooks/useAuth'

type BatchStatus = 'all' | 'upcoming' | 'ongoing' | 'completed'

export const InstructorBatchesPage: React.FC = () => {
    const navigate = useNavigate()
    const { user } = useAuth()
    const { data: batchesResponse, isLoading } = useAllBatches({})
    const [statusFilter, setStatusFilter] = useState<BatchStatus>('all')

    const batches = batchesResponse?.data || []

    const filteredBatches = batches.filter((b: any) => {
        const isAssigned = b.trainerId === user?.id
        const matchesStatus = statusFilter === 'all' || b.status === statusFilter
        return isAssigned && matchesStatus
    })

    const getStatusBadgeColor = (status: string) => {
        switch (status) {
            case 'upcoming': return 'bg-blue-500 text-white'
            case 'ongoing': return 'bg-emerald-500 text-white'
            case 'completed': return 'bg-slate-400 text-white'
            default: return 'bg-slate-400 text-white'
        }
    }

    return (
        <div>
            <PageHeader
                title="My Batches"
                subtitle="Manage your assigned batches and attendance"
            />

            {/* Status Filters */}
            <div className="flex flex-wrap items-center gap-2 mb-6">
                {(['all', 'upcoming', 'ongoing', 'completed'] as BatchStatus[]).map((status) => (
                    <button
                        key={status}
                        onClick={() => setStatusFilter(status)}
                        className={`px-4 py-2 rounded-lg text-xs font-bold uppercase transition-colors ${statusFilter === status
                                ? 'bg-primary text-white'
                                : 'bg-white dark:bg-surface-card text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white border border-gray-200 dark:border-white/5'
                            }`}
                    >
                        {status}
                    </button>
                ))}
            </div>

            {/* Batches List */}
            <div className="space-y-4">
                {isLoading ? (
                    <div className="text-center py-12 text-gray-500 dark:text-text-muted">Loading batches...</div>
                ) : filteredBatches.length === 0 ? (
                    <div className="text-center py-12 text-gray-500 dark:text-text-muted">
                        <div className="size-20 rounded-full bg-white dark:bg-surface-card mx-auto mb-4 flex items-center justify-center">
                            <span className="material-symbols-outlined text-[32px] text-gray-500">calendar_month</span>
                        </div>
                        <p className="text-gray-900 dark:text-white font-bold mb-2">No batches assigned</p>
                        <p className="text-sm">You haven't been assigned to any batches yet.</p>
                    </div>
                ) : (
                    filteredBatches.map((batch: any) => (
                        <div key={batch.id} className="rounded-lg bg-white dark:bg-surface-card border border-gray-200 dark:border-white/5 overflow-hidden hover:border-primary/20 transition-colors">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between p-5">
                                <div className="flex items-center gap-4">
                                    <div className="size-14 rounded-lg bg-primary/10 flex items-center justify-center">
                                        <span className="material-symbols-outlined text-primary text-[24px]">calendar_month</span>
                                    </div>
                                    <div>
                                        <h3 className="text-base font-bold text-gray-900 dark:text-white mb-1">{batch.title}</h3>
                                        <p className="text-xs text-gray-500 dark:text-text-muted mb-2">{batch.courseName || 'Course Name'}</p>
                                        <div className="flex items-center gap-4">
                                            <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-text-muted">
                                                <span className="material-symbols-outlined text-[14px]">calendar_today</span>
                                                <span>{batch.startDate || 'TBD'}</span>
                                            </div>
                                            <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-text-muted">
                                                <span className="material-symbols-outlined text-[14px]">group</span>
                                                <span>{batch.enrolledCount || 0} peserta</span>
                                            </div>
                                            <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold uppercase ${getStatusBadgeColor(batch.status)}`}>
                                                {batch.status}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <button
                                    onClick={() => navigate(`/instructor/batches/${batch.id}/attendance`)}
                                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-100 dark:bg-white/5 text-sm font-medium text-gray-900 dark:text-white hover:bg-gray-200 dark:hover:bg-white/10 transition-colors w-full sm:w-auto justify-center"
                                >
                                    <span className="material-symbols-outlined text-[18px]">checklist</span>
                                    Attendance
                                </button>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    )
}

export default InstructorBatchesPage
